import { Connection } from '@wix/cms-viewer-core'
import { ADDRESSINPUT_ROLE, BUTTON_ROLE, CHECKBOX_ROLE, IMAGE_ROLE, TEXT_ROLE } from '@wix/wix-data-client-common-standalone'
import { FieldType } from '@wix/wix-data-schema-types'

export const adaptLegacyConnection = (platformConnection, role) => {
  const { properties = {}, expressions = {}, behaviors = [], events = {}, totalCount } = platformConnection
  const newBehaviors = convertBehaviors(behaviors)
  const newEvents = convertEvents(events)
  const connection = Connection.make({ behaviors: { ...newBehaviors, ...newEvents } })

  for (const [propertyPath, { fieldName, format, ...rest }] of Object.entries(properties)) {
    connection.data = {
      ...connection.data,
      ...convertToData(rest),
    }
    const [fieldKey, ...valuePath] = fieldName.split('.')
    connection.properties[propertyPath] = Connection.makePropertyConnectionToField({
      fieldKey,
      path: valuePath.length === 0 ? undefined : valuePath,
      format,
      outputType: propertyPath === 'link' ? 'link' : getOutputType(role, propertyPath),
    })
  }

  for (const [path, { expression }] of Object.entries(expressions)) {
    connection.properties[path] = Connection.makePropertyConnectionToExpression({
      expression,
      outputType: getOutputType(role, path),
    })
  }

  if (totalCount) {
    const path = totalCount.prop
    connection.properties[path === '$text' ? 'text' : path] = Connection.makePropertyConnectionToDatasetMetadata({
      datasetMetadataKey: 'totalCount',
      outputType: 'text',
    })
  }

  return connection
}

const getOutputType = (role, path) => {
  const manifestDefaultFieldType = bindingDefinitions[role]?.[path]
  const outputType = fieldTypeToOutputType[manifestDefaultFieldType] ?? 'unknown'
  return outputType
}

const convertToData = ({ linkRel, linkTarget }) => {
  const dataEntries = []
  if (linkRel !== undefined) {
    dataEntries.push(['rel', linkRel])
  }
  if (linkTarget !== undefined) {
    dataEntries.push(['target', linkTarget])
  }
  return dataEntries.length > 0 ? Object.fromEntries(dataEntries) : {}
}

// In old contract behavior could be only one - success or failure
const convertBehaviors = ([behavior]) => behavior ? {
  onSubmit: {
    type: oldBehaviorTypeToNew[behavior.type],
    show: 'show',
    hide: 'hide',
  },
} : {}

const convertEvents = (events) => {
  let adaptedEvents = {}
  for (const [eventName, { action }] of Object.entries(events)) {
    if (eventName === 'onClick') {
      adaptedEvents.click = { onClick: 'onClick', action, disabled: 'disabled' }
    }
  }

  return adaptedEvents
}

const oldBehaviorTypeToNew = {
  saveSuccessFeedback: 'success',
  saveFailureFeedback: 'failure',
}

const bindingDefinitions = {
  [TEXT_ROLE]: {
    $text: FieldType.text,
    'style.color': FieldType.color,
  },
  [IMAGE_ROLE]: {
    src: FieldType.image,
    alt: FieldType.text,
    tooltip: FieldType.text,
    link: FieldType.url,
  },
  [BUTTON_ROLE]: {
    label: FieldType.text,
    link: FieldType.url,
    'accessibility.ariaLabel': FieldType.text,
    'style.color': FieldType.color,
    'style.iconColor': FieldType.color,
    'style.borderColor': FieldType.color,
    'style.backgroundColor': FieldType.color,
    'style.hover.color': FieldType.color,
    'style.hover.iconColor': FieldType.color,
    'style.hover.borderColor': FieldType.color,
    'style.hover.backgroundColor': FieldType.color,
    'style.disabled.color': FieldType.color,
    'style.disabled.iconColor': FieldType.color,
    'style.disabled.borderColor': FieldType.color,
    'style.disabled.backgroundColor': FieldType.color,
  },
  [CHECKBOX_ROLE]: { // TODO: is not supported by core yet
    checked: FieldType.boolean,
  },
  [ADDRESSINPUT_ROLE]: { // TODO: is not supported by core yet
    value: FieldType.address,
  },
}

const fieldTypeToOutputType = {
  [FieldType.text]: 'text',
  // despite there is dateType: 'image' in new component model,
  // old velo api expects wix:image://url as is, without any conversions
  [FieldType.image]: 'text',
  [FieldType.boolean]: 'boolean', // TODO: is not supported by core yet
  [FieldType.address]: 'schema', // TODO: is not supported by core yet. rethink output type name "schema"
  [FieldType.color]: 'unknown',
  [FieldType.url]: 'text',
}
